import { call, put, takeLatest, takeLeading } from "redux-saga/effects";
import { startLoading, stopLoading, getUserDetails } from "@shared/store/actions";
import { ActionWithPayload, WithCallback, WithNavigate } from "@shared/interfaces";
import { NamesOfParentRoutes } from "@shared/constants";
import history from "@shared/history";
import { tokenHandler } from "@shared/utils";
import { User } from "@shared/models";

import { logout, login, googleLogin, googleRegister, bootstrap } from "./actions";
import { AuthActionTypes } from "./constants";
import { GoogleLoginShape, LoginShape, LoginSuccess } from "../interfaces";
import api from "../api";

// Silent session restore on app load: the access token lives in memory (empty on a fresh load),
// so rebuild the session from the http-only refresh cookie. A 401 here is exempt from the request
// layer's retry/redirect (/auth/refresh is an auth endpoint), so it simply rejects → failure.
function* bootstrapSaga() {
  try {
    const { token }: LoginSuccess = yield call(api.refreshToken);

    if (token) {
      tokenHandler.set(token);
      yield put(bootstrap.success());
    } else {
      yield put(bootstrap.failure());
    }
  } catch {
    yield put(bootstrap.failure());
  }
}

function* logoutSaga() {
  // Best-effort server revoke while the access token + CSRF cookie are still present.
  try {
    yield call(api.logout);
  } catch {
    // ignore — clear the client session regardless of the server response.
  }

  tokenHandler.removeAll();
  const searchParams = window.location.search;
  localStorage.setItem("redirectUrl", `${window.location.pathname}${searchParams ? searchParams : ""}`);
  yield put(logout.success());
  yield put(getUserDetails.success(null));
  yield history.push(NamesOfParentRoutes.AUTH);
}

function* loginSaga({ payload }: ActionWithPayload<WithNavigate<LoginShape>>) {
  try {
    yield put(startLoading());
    const response: LoginSuccess = yield call(api.login, payload);
    const { token } = response;

    if (token) {
      yield tokenHandler.set(token);
      yield put(getUserDetails.request());
    }

    yield put(login.success(response));
    yield put(stopLoading());

    yield history.push("/");
  } catch (error) {
    yield put(login.failure(error as Error));
    yield put(stopLoading());
  }
}

function* googleLoginSaga({ payload }: ActionWithPayload<WithCallback<GoogleLoginShape>>) {
  try {
    const { cb, ...rest } = payload;
    yield put(startLoading());
    const response: User = yield call(api.googleLogin, rest);

    yield put(getUserDetails.success(response));

    yield put(stopLoading());
    if (cb) {
      cb();
    }
  } catch (error) {
    yield put(googleLogin.failure(error as Error));
    yield put(stopLoading());
  }
}

function* googleRegisterSaga({ payload }: ActionWithPayload<WithNavigate<GoogleLoginShape>>) {
  try {
    yield put(startLoading());
    const response: LoginSuccess = yield call(api.googleRegister, payload);
    const { token } = response;

    if (token) {
      yield tokenHandler.set(token);
      yield put(getUserDetails.request());
    }

    yield put(login.success(response));
    yield put(stopLoading());

    yield history.push("/");
  } catch (error) {
    yield put(googleRegister.failure(error as Error));
    yield put(stopLoading());
  }
}

function* authSagas() {
  // takeLeading (not takeLatest): a double-dispatch of BOOTSTRAP (StrictMode double-mount, HMR/
  // fast-refresh remount, or a future StrictMode adoption) would fire two concurrent /auth/refresh
  // calls that trip the API's refresh reuse-detection and revoke the whole session. takeLeading
  // runs the first and ignores the rest until it completes.
  yield takeLeading(AuthActionTypes.BOOTSTRAP, bootstrapSaga);
  yield takeLatest(AuthActionTypes.LOGOUT, logoutSaga);
  yield takeLatest(AuthActionTypes.LOGIN, loginSaga);
  yield takeLatest(AuthActionTypes.GOOGLE_LOGIN, googleLoginSaga);
  yield takeLatest(AuthActionTypes.GOOGLE_REGISTER, googleRegisterSaga);
}

export { bootstrapSaga, logoutSaga, googleRegisterSaga };
export default authSagas;
